import { useCallback, useEffect, useRef, useState } from "react";
import { useEditor, EditorContent, BubbleMenu } from "@tiptap/react";
import { baseExtensions, getMarkdown } from "@/components/rich/extensions";
import { RichToolbar } from "@/components/rich/RichToolbar";
import { PageBreaks, FootnoteMarks, paginate } from "@/components/studio/pagination";
import { PAGE_SIZES } from "@/lib/studio";

const MARGIN = 96;
const GAP = 32;
const SAFE = 20;

const Sheets = ({ pages, w, h }) => Array.from({ length: pages }, (_, i) => (
  <div key={i} className="absolute left-0 rounded-[2px] bg-[#F6F0E4] shadow-[0_10px_30px_rgba(0,0,0,0.45)]" style={{ top: i * (h + GAP), width: w, height: h }} data-testid={`page-sheet-${i + 1}`}>
    <div className="absolute inset-x-0 text-center text-[11px] tracking-widest text-[#8A7F6C]" style={{ bottom: MARGIN / 2 - 8 }} data-testid={`page-number-${i + 1}`}>{i + 1}</div>
  </div>
));

export const PageEditor = ({ value, onChange, disabled, pageSize = "letter", onPages, onEditor, extraTools, bubble, extensions = [], placeholder = "Begin your chapter…", stickyTop = 0, fluid = false }) => {
  const { w, h } = PAGE_SIZES[pageSize] || PAGE_SIZES.letter;
  const cfg = useRef({});
  cfg.current = { contentH: h - 2 * MARGIN - SAFE, stride: h + GAP, margin: MARGIN };
  const [pages, setPages] = useState(1);
  const last = useRef(value);
  const editor = useEditor({
    extensions: [...baseExtensions({ placeholder }), PageBreaks, FootnoteMarks, ...extensions],
    content: value,
    editable: !disabled,
    editorProps: { attributes: { class: `lf-prose lf-paper focus:outline-none${fluid ? " lf-fluid" : ""}`, style: fluid ? "padding:22px 40px 40px 18px;min-height:60vh" : `padding:${MARGIN}px`, "data-testid": "page-editor-content" } },
    onUpdate: ({ editor: ed }) => { last.current = getMarkdown(ed); onChange?.(last.current); },
  });
  const repaginate = useCallback(() => {
    if (!editor || editor.isDestroyed || fluid) return;
    requestAnimationFrame(() => {
      if (editor.isDestroyed) return;
      const n = paginate(editor.view, cfg.current);
      setPages(n);
      onPages?.(n);
    });
  }, [editor, onPages, fluid]);
  useEffect(() => {
    if (!editor) return undefined;
    editor.on("update", repaginate);
    editor.on("create", repaginate);
    window.addEventListener("resize", repaginate);
    document.fonts?.ready.then(repaginate);
    const ro = new ResizeObserver(repaginate);
    ro.observe(editor.view.dom);
    repaginate();
    return () => { ro.disconnect(); editor.off("update", repaginate); editor.off("create", repaginate); window.removeEventListener("resize", repaginate); };
  }, [editor, repaginate]);
  useEffect(() => { repaginate(); }, [pageSize, repaginate]);
  useEffect(() => {
    if (editor && value !== last.current) { last.current = value; editor.commands.setContent(value || "", false); repaginate(); }
  }, [editor, value, repaginate]);
  useEffect(() => { editor?.setEditable(!disabled, false); }, [editor, disabled]);
  useEffect(() => { onEditor?.(editor); }, [editor, onEditor]);
  const barRef = useRef(null);
  useEffect(() => {
    if (!editor) return undefined;
    const apply = () => {
      const top = stickyTop + (barRef.current?.offsetHeight || 0) + 24;
      editor.setOptions({ editorProps: { ...editor.options.editorProps, scrollMargin: { top, bottom: 32, left: 0, right: 0 }, scrollThreshold: { top, bottom: 32, left: 0, right: 0 } } });
    };
    apply();
    if (!barRef.current) return undefined;
    const ro = new ResizeObserver(apply);
    ro.observe(barRef.current);
    return () => ro.disconnect();
  }, [editor, stickyTop]);
  return (
    <div data-testid="page-editor">
      {!disabled && <div ref={barRef} className={`sticky top-0 z-20 mx-auto rounded-md border border-[#2D3442] bg-[#0F131A]/95 backdrop-blur-md ${fluid ? "mb-3" : "mb-6"}`} style={{ maxWidth: fluid ? undefined : w, top: stickyTop }}><RichToolbar editor={editor} testid="page-editor-toolbar" />{extraTools}</div>}
      {fluid ? <div className="relative rounded-sm bg-[#F6F0E4] shadow-[0_10px_30px_rgba(0,0,0,0.45)]" data-testid="page-editor-fluid"><EditorContent editor={editor} /></div> : <div className="overflow-x-auto lf-scroll">
      <div className="relative mx-auto pb-16" style={{ width: w, minHeight: pages * (h + GAP) - GAP }}>
        <Sheets pages={pages} w={w} h={h} />
        <div className="relative" style={{ width: w }}><EditorContent editor={editor} /></div>
        {bubble && editor && <BubbleMenu editor={editor} tippyOptions={{ duration: 120, placement: "top" }} shouldShow={({ state }) => !state.selection.empty}>{bubble(editor)}</BubbleMenu>}
      </div>
      </div>}
    </div>
  );
};
